import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import dayjs from 'lib/dayjs';
import toLowerCase from 'utils/toLowerCase';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { i18nExist } from 'services/i18n/utils';
import { ReturnOrderListItemV2 } from 'services/api/refund';

export type UseReturnItemDisplayStateParams = {
  row: Pick<
    ReturnOrderListItemV2,
    | 'displayStatus'
    | 'dueDateMs'
    | 'sellerEvidenceDeadlineMs'
    | 'offerDueDateMs'
    | 'sellerCompensationDueDateMs'
    | 'operate'
    | 'returnStatus'
    | 'returnType'
    | 'returnShipDueDateMs'
    | 'returnUpdateTime'
    | 'returnCreateTime'
  >;
  platform: General.PlatformUpperCase;
  operatable: boolean;
};

/** 各状态对应的截止时间戳 + 回应天数文案（供其他 hook 复用） */
export function useReturnItemNeedTimeTip(params: UseReturnItemDisplayStateParams) {
  const { row } = params;
  const { t } = useTranslate();
  const {
    displayStatus,
    dueDateMs,
    sellerEvidenceDeadlineMs,
    offerDueDateMs,
    sellerCompensationDueDateMs,
  } = row;

  const needTimeTip = useMemo(
    () => ({
      WAITING_EVIDENCE: sellerEvidenceDeadlineMs,
      PENDING_RESPOND: offerDueDateMs,
      REFUND_APPROVED: sellerCompensationDueDateMs,
      VALIDATION_IN_PROGRESS: dueDateMs,
    }),
    [sellerEvidenceDeadlineMs, offerDueDateMs, sellerCompensationDueDateMs, dueDateMs],
  );

  const replyDisplay = useMemo(() => {
    const day = dayjs(Number(needTimeTip[displayStatus as keyof typeof needTimeTip])).diff(
      dayjs(),
      'days',
    );
    return t('scene.orderWorkspace.returnRefundTab.detail.field.respondWithinDays', {
      day: day + 1,
    });
  }, [displayStatus, needTimeTip, t]);

  return { needTimeTip, replyDisplay };
}

/** 退货单状态文案（按平台翻译） */
export function useReturnItemStatusTip({
  displayStatus,
  returnStatus,
  returnType,
  platform,
}: {
  displayStatus: Conversation.Trade.RefundReason['displayStatus'];
  returnStatus: Conversation.Trade.RefundReason['returnStatus'];
  returnType: Conversation.Trade.RefundReason['returnType'];
  platform: General.PlatformUpperCase;
}) {
  const { t } = useTranslate();

  const returnStatusTip = useMemo(() => {
    if (platform === 'SHOPEE') {
      const shopeeDisplayStatus = displayStatus as Conversation.Trade.ShopeeDisplayStatus;
      const statusLabels: Record<string, string> = {
        IN_REVIEW: t('domain.shopeeRefund.displayStatus.inReview'),
        WAITING_EVIDENCE: t('domain.shopeeRefund.displayStatus.waitingEvidence'),
        RETURN_PROCESSING: t('domain.shopeeRefund.displayStatus.returnProcessing'),
        PENDING_RESPOND: t('domain.shopeeRefund.displayStatus.pendingRespond'),
        REFUND_APPROVED: t('domain.shopeeRefund.displayStatus.refundApproved'),
        WAITING_BUYER_RESPONSE: t('domain.shopeeRefund.displayStatus.waitingBuyerResponse'),
        RETURN_IN_PROGRESS: t('domain.shopeeRefund.displayStatus.returnInProgress'),
        REFUND_COMPLETED: t('domain.shopeeRefund.displayStatus.refundCompleted'),
        DISPUTE_RESOLVED: t('domain.shopeeRefund.displayStatus.disputeResolved'),
        CANCELLED: t('domain.shopeeRefund.displayStatus.cancelled'),
        UNKNOWN: t('domain.shopeeRefund.displayStatus.unknown', { platform: 'Shopee' }),
        REFUND_PENDING: t('domain.shopeeRefund.displayStatus.refundPending'),
        RETURN_REFUND_PENDING: t('domain.shopeeRefund.displayStatus.returnRefundPending'),
        REFUND_REQUESTED: t('domain.shopeeRefund.displayStatus.refundRequested'),
        VALIDATION_IN_PROGRESS: t('domain.shopeeRefund.displayStatus.validationInProgress'),
        VALIDATION_IN_PROGRESS_V2: t('domain.shopeeRefund.displayStatus.validationInProgress'),
        EVIDENCE_SUBMITTED: t('domain.shopeeRefund.displayStatus.evidenceSubmitted'),
        COMPENSATION_APPROVED: t('domain.shopeeRefund.displayStatus.compensationApproved'),
        DISPUTE_REJECT: t('domain.shopeeRefund.displayStatus.disputeReject'),
        DISPUTE_WAIT_EVIDENCE: t('domain.shopeeRefund.displayStatus.disputeWaitEvidence'),
        REFUND_APPROVED_2: t('domain.shopeeRefund.displayStatus.refundApproved'),
      };
      return statusLabels[shopeeDisplayStatus] ?? shopeeDisplayStatus;
    }

    if (platform === 'LAZADA') {
      return t(`${toLowerCase(platform)}.${returnStatus}`);
    }

    if (isTiKTokUpperCase(platform)) {
      const rtStatus = returnStatus === '1' ? `1_${returnType}` : returnStatus;
      return t(`tiktok.${rtStatus}`);
    }

    if (platform === 'MERCADO') {
      return t(`mercado.returnStatus.${displayStatus}`);
    }

    if (platform === 'KWAI') {
      return i18nExist(`kwai.returnStatus.${returnStatus}`)
        ? t(`kwai.returnStatus.${returnStatus}`)
        : '-';
    }

    return returnStatus;
  }, [displayStatus, platform, returnStatus, returnType, t]);

  return { returnStatusTip };
}

interface UseTikTokTimeTipParams {
  platform: General.PlatformUpperCase;
  dueDateMs: string;
  operatable: boolean;
  operate: Conversation.Trade.IReturnOperationType[];
}

export function useTikTokTimeTip(params: UseTikTokTimeTipParams) {
  const { platform, dueDateMs, operatable, operate } = params;
  const { t } = useTranslate();

  /** TikTok 倒计时提示 */
  const tkTimeTip = useMemo(() => {
    const tkOperateList = [
      'FULL_REFUND',
      'CONFIRM_ONLY_REFUND',
      'REFUSE',
      'REFUSE_CHANGE',
      'CONFIRM_RETURN_REFUND',
      'CONFIRM_CHANGE',
      'FAKE_REFUSE',
    ];
    if (
      !isTiKTokUpperCase(platform) ||
      !dueDateMs ||
      dueDateMs === '0' ||
      !operatable ||
      tkOperateList.every((_) => !operate.includes(_))
    ) {
      return null;
    }
    const time = dayjs(Number(dueDateMs));
    const now = dayjs();
    const hours = Math.max(time.diff(now, 'hours'), 0);
    const minutes = Math.max(time.diff(now, 'minutes') % 60, 0);
    return {
      text: t('scene.orderWorkspace.returnRefundTab.list.column.remainingQuantity', {
        x: ` ${hours}h ${minutes}m`,
      }),
    };
  }, [platform, dueDateMs, operatable, operate, t]);

  return { tkTimeTip };
}

/** Shopee 红色时间提示 + TikTok 倒计时提示（由视图层渲染 SpecialStatusTimetip） */
export function useReturnItemSpecialTimeTips(params: UseReturnItemDisplayStateParams) {
  const { row, platform, operatable } = params;
  const { needTimeTip, replyDisplay } = useReturnItemNeedTimeTip(params);
  const { displayStatus, dueDateMs, operate } = row;

  const { tkTimeTip } = useTikTokTimeTip({ platform, dueDateMs, operatable, operate });

  /** Shopee 需在 X 天内回应时的红色时间提示 */
  const shopeeTimeTip = useMemo(() => {
    const timeTip = needTimeTip[displayStatus as keyof typeof needTimeTip];
    if (
      platform === 'SHOPEE' &&
      timeTip &&
      dayjs(Number(timeTip)).diff(dayjs(), 'days') > -1 &&
      ['WAITING_EVIDENCE', 'PENDING_RESPOND', 'REFUND_APPROVED', 'VALIDATION_IN_PROGRESS'].includes(
        displayStatus,
      )
    ) {
      return { replyDisplay };
    }
    return null;
  }, [displayStatus, needTimeTip, platform, replyDisplay]);

  return { shopeeTimeTip, tkTimeTip };
}

/** 各平台格式化后的时间展示文案 */
export function useReturnItemFormattedTimes(params: UseReturnItemDisplayStateParams) {
  const { row, platform } = params;
  const { t } = useTranslate();
  const { needTimeTip } = useReturnItemNeedTimeTip(params);
  const { displayStatus, dueDateMs, returnShipDueDateMs, returnUpdateTime, returnCreateTime } = row;

  const formattedShopeeTime = useMemo(() => {
    const shopeeNeedTipStatuses = [
      'WAITING_EVIDENCE',
      'PENDING_RESPOND',
      'REFUND_APPROVED',
      'VALIDATION_IN_PROGRESS',
    ];
    return platform === 'SHOPEE' &&
      shopeeNeedTipStatuses.includes(displayStatus) &&
      needTimeTip[displayStatus as keyof typeof needTimeTip] !== '0'
      ? t('scene.orderWorkspace.returnRefundTab.detail.field.respondDeadline', {
          time: dayjs(Number(needTimeTip[displayStatus as keyof typeof needTimeTip])).format(
            'YYYY-MM-DD HH:mm:ss',
          ),
        })
      : '';
  }, [platform, displayStatus, needTimeTip, t]);

  const formattedNonMercadoTime = useMemo(
    () =>
      platform !== 'MERCADO' && (dueDateMs !== '0' || returnShipDueDateMs !== '0')
        ? dayjs(Number(returnShipDueDateMs) || Number(dueDateMs)).format('YYYY-MM-DD HH:mm:ss')
        : '',
    [platform, dueDateMs, returnShipDueDateMs],
  );

  const formattedTikTokOrKwaiTime = useMemo(
    () =>
      (isTiKTokUpperCase(platform) || platform === 'KWAI') && (returnUpdateTime || returnCreateTime)
        ? dayjs(Number(returnUpdateTime || returnCreateTime)).format('YYYY-MM-DD HH:mm:ss')
        : '',
    [platform, returnUpdateTime, returnCreateTime],
  );

  const formattedMercadoTime = useMemo(
    () =>
      platform === 'MERCADO' &&
      (displayStatus === 'REVERSE_PENDING' || displayStatus === 'WAITING_BUYER_RETURN') &&
      dueDateMs &&
      dueDateMs !== '0'
        ? `${t('scene.orderWorkspace.returnRefundTab.detail.field.responseDeadline')}: ${dayjs(
            Number(dueDateMs),
          ).format('YYYY-MM-DD')}`
        : '',
    [platform, displayStatus, dueDateMs, t],
  );

  return {
    formattedShopeeTime,
    formattedNonMercadoTime,
    formattedTikTokOrKwaiTime,
    formattedMercadoTime,
  };
}

/** 退货单条目的展示状态（组合 hook，兼容原有调用方） */
export function useReturnItemDisplayState(params: UseReturnItemDisplayStateParams) {
  const { needTimeTip } = useReturnItemNeedTimeTip(params);
  const { returnStatusTip } = useReturnItemStatusTip({
    displayStatus: params.row.displayStatus,
    returnStatus: params.row.returnStatus,
    returnType: params.row.returnType,
    platform: params.platform,
  });
  const { shopeeTimeTip, tkTimeTip } = useReturnItemSpecialTimeTips(params);
  const {
    formattedShopeeTime,
    formattedNonMercadoTime,
    formattedTikTokOrKwaiTime,
    formattedMercadoTime,
  } = useReturnItemFormattedTimes(params);

  return {
    needTimeTip,
    returnStatusTip,
    shopeeTimeTip,
    tkTimeTip,
    formattedShopeeTime,
    formattedNonMercadoTime,
    formattedTikTokOrKwaiTime,
    formattedMercadoTime,
  };
}
